import { useQuery } from '@apollo/client';
import localforage from 'localforage';
import * as _ from 'lodash-es';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import populatePostsWithFullTopics from './populatePostsWithFullTopics';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { useAllTopicMap } from '../topic/useAllTopics';
import useIsPublicView from '../utils/useIsPublicView';

// eslint-disable-next-line slab-graphql/no-unused-fields, slab-graphql/no-unused-colocated-fragments
gql(`
  fragment AllSlimPostFields on SlimPostFields {
    id
    title
    type
    privacy
    views
    owner {
      id
    }
    series {
      id
    }
    topics {
      id
    }
    contributors {
      id
    }
    linkAccess
    archivedAt
    editedAt
    insertedAt
    publishedAt
    updatedAt
    verifiedAt
    userViewedAt
    ...PostEntityIcon_post
    ...populatePostsWithFullTopics_posts
    ...PostTitleWithIcons_post
  }
`);

const ALL_SLIM_POSTS_FRAGMENT = gql(`
  fragment AllSlimPosts on SlimPost {
    id
    ...AllSlimPostFields
  }
`);

const ALL_SLIM_POSTS = gql(/** @complexity 130 */ `
  query allSlimPosts {
    session: currentSession {
      organization {
        id
        posts {
          id
          ...AllSlimPosts
        }
      }
    }
  }
`);

const ALL_SLIM_POSTS_KEY = 'all_slim_posts';

export type SlimPostWithTopics = Omit<
  DocumentType<typeof ALL_SLIM_POSTS_FRAGMENT>,
  'topics'
> & {
  topics: ReturnType<typeof useAllTopicMap>[string][];
};

const AllSlimPostsContext = createContext<{
  allSlimPosts: DocumentType<typeof ALL_SLIM_POSTS_FRAGMENT>[];
  allSlimPostsWithTopics: SlimPostWithTopics[];
  allSlimPostsWithTopicsMap: Record<string, SlimPostWithTopics>;
}>({
  allSlimPosts: [],
  allSlimPostsWithTopics: [],
  allSlimPostsWithTopicsMap: {},
});

interface AllSlimPostsProviderProps {
  children: React.ReactNode;
}

export const AllSlimPostsProvider = ({
  children,
}: AllSlimPostsProviderProps) => {
  const allTopicsMap = useAllTopicMap();
  const [slimPostsCache, setSlimPostsCache] = useState<
    DocumentType<typeof ALL_SLIM_POSTS_FRAGMENT>[]
  >([]);

  const isNonVisitorUser = useIsNonVisitorUser();
  const isPublicView = useIsPublicView();

  const { data } = useQuery(ALL_SLIM_POSTS, {
    fetchPolicy: 'no-cache',
    skip: !isNonVisitorUser || isPublicView,
  });

  useEffect(() => {
    localforage.getItem<string>(ALL_SLIM_POSTS_KEY).then((value) => {
      const parsedValue = value ? JSON.parse(value) : null;
      if (parsedValue) {
        setSlimPostsCache(parsedValue);
      }
    });
  }, []);

  const posts = data?.session?.organization?.posts;

  useEffect(() => {
    if (!posts) {
      return;
    }

    localforage.setItem(ALL_SLIM_POSTS_KEY, JSON.stringify(posts));
  }, [posts]);

  const value = useMemo(() => {
    const allSlimPosts = posts ?? slimPostsCache;
    const allSlimPostsWithTopics = populatePostsWithFullTopics(
      allSlimPosts,
      allTopicsMap,
    );

    return {
      allSlimPosts,
      allSlimPostsWithTopics,
      allSlimPostsWithTopicsMap: _.keyBy(allSlimPostsWithTopics, 'id'),
    };
  }, [posts, slimPostsCache, allTopicsMap]);

  return (
    <AllSlimPostsContext.Provider value={value}>
      {children}
    </AllSlimPostsContext.Provider>
  );
};

const useAllSlimPosts = () =>
  useContext(AllSlimPostsContext).allSlimPostsWithTopics;

export const useAllSlimPostsWithoutTopics = () =>
  useContext(AllSlimPostsContext).allSlimPosts;

export const useAllSlimPostMap = () =>
  useContext(AllSlimPostsContext).allSlimPostsWithTopicsMap;

export default useAllSlimPosts;
